import axios from 'axios';
import {
    CRUT_V1_API_ROOT,
    CRUT_V2_API_ROOT,
    CAPITAL_GAINS_COMPARISON_API_ROOT,
    ORDINARY_INCOME_COMPARISON_API_ROOT,
    ESTATE_COMPARISON_API_ROOT,
    ESTATE_BURN_COMPARISON_API_ROOT,
    GRAT_V2_API_ROOT,
    IRA_API_ROOT,
    NGT_API_ROOT,
    OIL_GAS_API_ROOT,
    PPLI_API_ROOT,
    QSBS_API_ROOT,
    SOLAR_API_ROOT,
    logApiError,
} from './api';
import { MODEL_FORM_VERSIONS } from './model-schema';

const wrap = (key, data) => ({ data, version: MODEL_FORM_VERSIONS[key] });
const unwrap = entry => (entry && entry.data) || {};

export default {
    namespaced: true,
    state: {
        clatModelForm: wrap('clatModelForm', {}),
        crutReportData: {},
        crutModelForm: wrap('crutModelForm', {}),
        crutModelLength: 'max_length',
        crutModelStrategy: 'nimcrut',
        crutDataSource: null,
        gratModelForm: wrap('gratModelForm', {}),
        gratDataSource: null,
        iraModelForm: wrap('iraModelForm', {}),
        oilGasModelForm: wrap('oilGasModelForm', {}),
        oilGasDataSource: null,
        ppliModelForm: wrap('ppliModelForm', {}),
        qsbsModelForm: wrap('qsbsModelForm', {}),
        solarModelForm: wrap('solarModelForm', {}),
        solarDataSource: null,
        totalOrdinaryIncome: null,
        totalLtcgIncome: null,
        ngtModelForm: wrap('ngtModelForm', {}),
        estateBurnModelForm: wrap('estateBurnModelForm', {}),
    },
    mutations: {
        updateClatModelForm(state, data) {
            state.clatModelForm = wrap('clatModelForm', data);
        },
        updateCrutModelForm(state, data) {
            state.crutModelForm = wrap('crutModelForm', data);
        },
        setCrutDataSource(state, source) {
            state.crutDataSource = source;
        },
        clearCrutDataSource(state) {
            state.crutDataSource = null;
        },
        updateCrutModelLength(state, data) {
            state.crutModelLength = data;
        },
        updateCrutModelStrategy(state, data) {
            state.crutModelStrategy = data;
        },
        updateCrutReportData(state, data) {
            state.crutReportData = data;
        },
        updateGratModelForm(state, data) {
            state.gratModelForm = wrap('gratModelForm', data);
        },
        setGratDataSource(state, source) {
            state.gratDataSource = source;
        },
        clearGratDataSource(state) {
            state.gratDataSource = null;
        },
        updateIraModelForm(state, data) {
            state.iraModelForm = wrap('iraModelForm', data);
        },
        updateOilGasModelForm(state, data) {
            state.oilGasModelForm = wrap('oilGasModelForm', data);
        },
        setOilGasDataSource(state, source) {
            state.oilGasDataSource = source;
        },
        clearOilGasDataSource(state) {
            state.oilGasDataSource = null;
        },
        updatePpliModelForm(state, data) {
            state.ppliModelForm = wrap('ppliModelForm', data);
        },
        updateQsbsModelForm(state, data) {
            state.qsbsModelForm = wrap('qsbsModelForm', data);
        },
        updateSolarModelForm(state, data) {
            state.solarModelForm = wrap('solarModelForm', data);
        },
        setSolarDataSource(state, source) {
            state.solarDataSource = source;
        },
        clearSolarDataSource(state) {
            state.solarDataSource = null;
        },
        updateTotalOrdinaryIncome(state, data) {
            state.totalOrdinaryIncome = data;
        },
        updateTotalLtcgIncome(state, data) {
            state.totalLtcgIncome = data;
        },
        updateNgtModelForm(state, data) {
            state.ngtModelForm = wrap('ngtModelForm', data);
        },
        updateEstateBurnModelForm(state, data) {
            state.estateBurnModelForm = wrap('estateBurnModelForm', data);
        },
    },
    getters: {
        clat_model_form: state => unwrap(state.clatModelForm),
        crut_report_data: state => {
            return state.crutReportData || {};
        },
        crut_model_form: state => unwrap(state.crutModelForm),
        crut_data_source: state => {
            return state.crutDataSource;
        },
        crut_model_length: state => {
            return state.crutModelLength || '';
        },
        crut_model_strategy: state => {
            return state.crutModelStrategy || '';
        },
        grat_model_form: state => unwrap(state.gratModelForm),
        grat_data_source: state => {
            return state.gratDataSource;
        },
        ira_model_form: state => unwrap(state.iraModelForm),
        oil_gas_model_form: state => unwrap(state.oilGasModelForm),
        oil_gas_data_source: state => {
            return state.oilGasDataSource;
        },
        ppli_model_form: state => unwrap(state.ppliModelForm),
        qsbs_model_form: state => unwrap(state.qsbsModelForm),
        solar_model_form: state => unwrap(state.solarModelForm),
        solar_data_source: state => {
            return state.solarDataSource;
        },
        total_ordinary_income: state => {
            return state.totalOrdinaryIncome || 0;
        },
        total_ltcg_income: state => {
            return state.totalLtcgIncome || 0;
        },
        ngt_model_form: state => unwrap(state.ngtModelForm),
        estate_burn_model_form: state => unwrap(state.estateBurnModelForm),
    },
    actions: {
        async calculateClat({ dispatch }, payload) {
            try {
                const modelBase = CRUT_V1_API_ROOT.split('/api/')[0];
                const response = await axios.post(`${modelBase}/api/v2/clat/calculate/`, payload);
                return {
                    defaultData: response['data']['defaultData'],
                    trustData: response['data']['trustData'],
                    dafData: response['data']['dafData'],
                };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async fetchCrutTwoLifeAvailability({ dispatch }) {
            try {
                const response = await axios.get(`${CRUT_V2_API_ROOT}/two-life-availability`);
                return response['data'];
            } catch (e) {
                logApiError(e, dispatch);
                return null;
            }
        },
        async calculateCrut({ dispatch }, payload) {
            try {
                const postTwoLives = (url, extra = {}) =>
                    payload.two_lives_selected && payload.spouse_age
                        ? axios.post(url, { ...payload, ...extra, is_two_lives: true }).catch(() => null)
                        : Promise.resolve(null);

                const [
                    defaultLifetimeNimcrutData,
                    defaultMaxLengthNimcrutData,
                    defaultTermNimcrutData,
                    defaultLifetimeScrutData,
                    defaultMaxLengthScrutData,
                    defaultTermScrutData,
                    defaultLifetimeFlipCrutData,
                    defaultMaxLengthFlipCrutData,
                    defaultTermFlipCrutData,
                    scrutLifetimeData,
                    scrutMaxLengthData,
                    scrutTermData,
                    nimcrutLifetimeData,
                    nimcrutMaxLengthData,
                    nimcrutTermData,
                    flipCrutLifetimeData,
                    flipCrutMaxLengthData,
                    flipCrutTermData,
                    defaultTwoLivesNimcrutData,
                    defaultTwoLivesScrutData,
                    defaultTwoLivesFlipCrutData,
                    scrutTwoLivesData,
                    nimcrutTwoLivesData,
                    flipCrutTwoLivesData,
                ] = await Promise.all([
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, {
                        ...payload,
                        compare_trust_type: 'NIMCRUT',
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, {
                        ...payload,
                        compare_trust_type: 'NIMCRUT',
                        is_max_length: true,
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/term`, {
                        ...payload,
                        compare_trust_type: 'NIMCRUT',
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, {
                        ...payload,
                        compare_trust_type: 'SCRUT',
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, {
                        ...payload,
                        compare_trust_type: 'SCRUT',
                        is_max_length: true,
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/term`, {
                        ...payload,
                        compare_trust_type: 'SCRUT',
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, {
                        ...payload,
                        compare_trust_type: 'FLIP_CRUT',
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, {
                        ...payload,
                        compare_trust_type: 'FLIP_CRUT',
                        is_max_length: true,
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/default/term`, {
                        ...payload,
                        compare_trust_type: 'FLIP_CRUT',
                    }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/scrut/lifetime`, payload),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/scrut/lifetime`, { ...payload, is_max_length: true }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/scrut/term`, payload),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/nimcrut/lifetime`, payload),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/nimcrut/lifetime`, { ...payload, is_max_length: true }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/nimcrut/term`, payload),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/flip_crut/lifetime`, payload),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/flip_crut/lifetime`, { ...payload, is_max_length: true }),
                    axios.post(`${CRUT_V2_API_ROOT}/calculate/flip_crut/term`, payload),
                    postTwoLives(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, { compare_trust_type: 'NIMCRUT' }),
                    postTwoLives(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, { compare_trust_type: 'SCRUT' }),
                    postTwoLives(`${CRUT_V2_API_ROOT}/calculate/default/lifetime`, { compare_trust_type: 'FLIP_CRUT' }),
                    postTwoLives(`${CRUT_V2_API_ROOT}/calculate/scrut/lifetime`),
                    postTwoLives(`${CRUT_V2_API_ROOT}/calculate/nimcrut/lifetime`),
                    postTwoLives(`${CRUT_V2_API_ROOT}/calculate/flip_crut/lifetime`),
                ]);

                return {
                    defaultLifetimeNimcrutData: defaultLifetimeNimcrutData['data'],
                    defaultMaxLengthNimcrutData: defaultMaxLengthNimcrutData['data'],
                    defaultTermNimcrutData: defaultTermNimcrutData['data'],
                    defaultLifetimeScrutData: defaultLifetimeScrutData['data'],
                    defaultMaxLengthScrutData: defaultMaxLengthScrutData['data'],
                    defaultTermScrutData: defaultTermScrutData['data'],
                    defaultLifetimeFlipCrutData: defaultLifetimeFlipCrutData['data'],
                    defaultMaxLengthFlipCrutData: defaultMaxLengthFlipCrutData['data'],
                    defaultTermFlipCrutData: defaultTermFlipCrutData['data'],
                    scrutLifetimeData: scrutLifetimeData['data'],
                    scrutMaxLengthData: scrutMaxLengthData['data'],
                    scrutTermData: scrutTermData['data'],
                    nimcrutLifetimeData: nimcrutLifetimeData['data'],
                    nimcrutMaxLengthData: nimcrutMaxLengthData['data'],
                    nimcrutTermData: nimcrutTermData['data'],
                    flipCrutLifetimeData: flipCrutLifetimeData['data'],
                    flipCrutMaxLengthData: flipCrutMaxLengthData['data'],
                    flipCrutTermData: flipCrutTermData['data'],
                    defaultTwoLivesNimcrutData: defaultTwoLivesNimcrutData?.['data'] || null,
                    defaultTwoLivesScrutData: defaultTwoLivesScrutData?.['data'] || null,
                    defaultTwoLivesFlipCrutData: defaultTwoLivesFlipCrutData?.['data'] || null,
                    scrutTwoLivesData: scrutTwoLivesData?.['data'] || null,
                    nimcrutTwoLivesData: nimcrutTwoLivesData?.['data'] || null,
                    flipCrutTwoLivesData: flipCrutTwoLivesData?.['data'] || null,
                };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateIra({ dispatch }, payload) {
            try {
                const defaultLifetimePromise = axios.post(`${IRA_API_ROOT}/calculate/default/lifetime`, payload);
                const defaultTermPromise = axios.post(`${IRA_API_ROOT}/calculate/default/term`, payload);
                const standardCrutLifetimePromise = axios.post(
                    `${IRA_API_ROOT}/calculate/standard_crut/lifetime`,
                    payload,
                );
                const standardCrutTermPromise = axios.post(`${IRA_API_ROOT}/calculate/standard_crut/term`, payload);
                const nimcrutLifetimePromise = axios.post(`${IRA_API_ROOT}/calculate/nimcrut/lifetime`, payload);
                const nimcrutTermPromise = axios.post(`${IRA_API_ROOT}/calculate/nimcrut/term`, payload);

                const defaultLifetimeData = await defaultLifetimePromise;
                const defaultTermData = await defaultTermPromise;
                const standardCrutLifetimeData = await standardCrutLifetimePromise;
                const standardCrutTermData = await standardCrutTermPromise;
                const nimcrutLifetimeData = await nimcrutLifetimePromise;
                const nimcrutTermData = await nimcrutTermPromise;

                return {
                    defaultLifetimeData: defaultLifetimeData['data'],
                    defaultTermData: defaultTermData['data'],
                    standardCrutLifetimeData: standardCrutLifetimeData['data'],
                    standardCrutTermData: standardCrutTermData['data'],
                    nimcrutLifetimeData: nimcrutLifetimeData['data'],
                    nimcrutTermData: nimcrutTermData['data'],
                };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateGrat({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${GRAT_V2_API_ROOT}/calculate/`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateOilGas({ dispatch }, payload) {
            try {
                const defaultPromise = axios.post(`${OIL_GAS_API_ROOT}/calculate/default`, payload);
                const scenarioPromise = axios.post(`${OIL_GAS_API_ROOT}/calculate/scenario`, payload);

                const defaultData = await defaultPromise;
                const scenarioData = await scenarioPromise;

                return {
                    defaultData: defaultData['data'],
                    scenarioData: scenarioData['data'],
                };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculatePpli({ dispatch }, payload) {
            try {
                const estateTaxData = await axios.post(
                    `${PPLI_API_ROOT}/calculate/estate_tax_arrears/insurance`,
                    payload,
                );
                const scenarioData = await axios.post(`${PPLI_API_ROOT}/calculate/scenario/insurance`, payload);

                return {
                    estateTaxData: estateTaxData['data'],
                    scenarioData: scenarioData['data'],
                };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateQsbs({ dispatch }, payload) {
            try {
                const defaultPromise = axios.post(`${QSBS_API_ROOT}/calculate/default`, payload);
                const trustPromise = axios.post(`${QSBS_API_ROOT}/calculate/trust`, payload);

                const defaultData = await defaultPromise;
                const trustData = await trustPromise;

                return {
                    defaultData: defaultData['data'],
                    trustData: trustData['data'],
                };
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateSolarFlipPartnershipProject({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${SOLAR_API_ROOT}/projects/flip_partnership/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateSolarSaleLeasebackProject({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${SOLAR_API_ROOT}/projects/sale_leaseback/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateSolarScenario({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${SOLAR_API_ROOT}/scenario/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateSolarTaxSummary({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${SOLAR_API_ROOT}/tax_benefits_summary/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async comparisonCalculatorCapitalGains({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${CAPITAL_GAINS_COMPARISON_API_ROOT}/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async comparisonCalculatorEstate({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${ESTATE_COMPARISON_API_ROOT}/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async comparisonCalculatorOrdinaryIncome({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${ORDINARY_INCOME_COMPARISON_API_ROOT}/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateNgt({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${NGT_API_ROOT}/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async calculateEstateBurn({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${ESTATE_BURN_COMPARISON_API_ROOT}/calculate`, payload);

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
    },
};
